/**
 * delay-delivery-cart.js (My Divan Bed)
 * One "Delay Delivery?" choice for the whole order, stored as a single marked line inside
 * cart.note (the same order-note field as the theme's own "Add order note" box).
 * Copied from Everest Beds, with two MDB changes:
 *  1. The theme's cart-note box posts its whole textarea value on every keystroke, which would
 *     wipe our line if the textarea still held the old note. After each update we mirror the new
 *     note into those textareas so the line survives customer edits.
 *  2. A MutationObserver syncs any freshly rendered widget (the cart drawer and cart footer are
 *     re-rendered by the theme after cart changes).
 * Only our one marked line is ever touched, so customer-written note text is preserved.
 */
(function () {
  'use strict';

  var WEEK_COUNT = 12;
  var MARKER_PREFIX = 'Delay delivery requested - ';
  var MARKER_RE = /^Delay delivery requested [-–—] Week commencing .+$/m;

  var isSyncing = false;

  function j(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('[DD] ' + url + ' -> ' + r.status);
      return r.json();
    });
  }

  function getUpcomingWeeks(count) {
    var weeks = [];
    var now = new Date();
    var day = now.getDay();
    var diffToNextMonday = ((8 - day) % 7) || 7;
    var monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToNextMonday);
    var fmt = function (d) { return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); };
    for (var i = 0; i < count; i++) {
      var start = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i * 7);
      var value = 'Week commencing ' + fmt(start);
      weeks.push({ value: value, label: value });
    }
    return weeks;
  }

  function populateWeekSelect(select) {
    if (select.dataset.populated) return;
    var weeks = getUpcomingWeeks(WEEK_COUNT);
    select.innerHTML = weeks.map(function (w) {
      return '<option value="' + w.value.replace(/"/g, '&quot;') + '">' + w.label + '</option>';
    }).join('');
    select.dataset.populated = 'true';
  }

  function renderWidget(widget, choice, week) {
    widget.querySelectorAll('.dd__opt').forEach(function (btn) {
      var isSel = btn.dataset.value === choice;
      btn.classList.toggle('dd__opt--selected', isSel);
      btn.setAttribute('aria-checked', String(isSel));
    });
    var selectedEl = widget.querySelector('.dd__selected');
    var weekWrap = widget.querySelector('.dd__week-wrap');
    var select = widget.querySelector('.dd__week-select');
    if (choice === 'Yes') {
      weekWrap.hidden = false;
      populateWeekSelect(select);
      if (week && Array.from(select.options).some(function (o) { return o.value === week; })) {
        select.value = week;
      }
      if (selectedEl) selectedEl.textContent = select.value || 'Yes';
    } else {
      weekWrap.hidden = true;
      if (selectedEl) selectedEl.textContent = 'No';
    }
  }

  function renderAll(choice, week) {
    document.querySelectorAll('.dd').forEach(function (w) { renderWidget(w, choice, week); });
  }

  function buildNote(existingNote, choice, week) {
    var note = (existingNote || '').replace(MARKER_RE, '').replace(/\n{3,}/g, '\n\n').trim();
    if (choice === 'Yes' && week) {
      var line = MARKER_PREFIX + week;
      note = note ? (note + '\n\n' + line) : line;
    }
    return note;
  }

  function mirrorNoteToTextareas(note) {
    document.querySelectorAll('cart-note textarea, textarea[name="note"]').forEach(function (t) {
      if (t.value !== note) t.value = note;
    });
  }

  var updateTimer = null;
  var pendingState = null;

  function updateCart(choice, week) {
    pendingState = { choice: choice, week: week };
    clearTimeout(updateTimer);
    updateTimer = setTimeout(flushUpdate, 250);
  }

  function flushUpdate() {
    if (!pendingState || isSyncing) return;
    var state = pendingState;
    pendingState = null;
    isSyncing = true;
    var newNote = '';
    j('/cart.js')
      .then(function (cart) {
        newNote = buildNote(cart.note, state.choice, state.week);
        return j('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: newNote })
        });
      })
      .then(function () {
        mirrorNoteToTextareas(newNote);
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('theme:cart:updated'));
      })
      .catch(function (e) { console.warn(e); })
      .then(function () {
        isSyncing = false;
        if (pendingState) flushUpdate();
      });
  }

  function syncFromCart() {
    j('/cart.js').then(function (cart) {
      var m = (cart.note || '').match(MARKER_RE);
      if (m) {
        renderAll('Yes', m[0].replace(/^Delay delivery requested [-–—] /, ''));
      } else {
        renderAll('No', '');
      }
    }).catch(function (e) { console.warn(e); });
  }

  function bind() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.dd__opt');
      if (!btn) return;
      var widget = btn.closest('.dd');
      var choice = btn.dataset.value;
      var select = widget.querySelector('.dd__week-select');
      var week = choice === 'Yes' ? (select.value || null) : null;
      if (choice === 'Yes' && !week) {
        populateWeekSelect(select);
        week = select.value;
      }
      renderAll(choice, week);
      updateCart(choice, week);
    });

    document.addEventListener('change', function (e) {
      var select = e.target.closest && e.target.closest('.dd__week-select');
      if (!select) return;
      renderAll('Yes', select.value);
      updateCart('Yes', select.value);
    });

    ['cart:updated', 'cart:refresh', 'theme:cart:updated', 'theme:cart:refresh', 'theme:cart:added', 'cart-drawer:open', 'shopify:section:load']
      .forEach(function (ev) { document.addEventListener(ev, syncFromCart); });

    if (window.MutationObserver) {
      var syncTimer = null;
      new MutationObserver(function () {
        var fresh = document.querySelector('.dd:not([data-dd-seen])');
        if (!fresh) return;
        document.querySelectorAll('.dd').forEach(function (w) { w.setAttribute('data-dd-seen', '1'); });
        clearTimeout(syncTimer);
        syncTimer = setTimeout(syncFromCart, 60);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  function init() {
    if (window.__ddBound) return;
    window.__ddBound = true;
    bind();
    if (document.querySelector('.dd')) {
      document.querySelectorAll('.dd').forEach(function (w) { w.setAttribute('data-dd-seen', '1'); });
      syncFromCart();
    }
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
})();
